/**
 * SketchRibbon — a vertical gallery of matted sketches/renderings with a tap-to-enlarge lightbox.
 * The client-facing read view of a journey "Sketch ribbon" entry (data.sketches = array of
 * { url, title, type, legend, isDrawing? }). Self-contained + inline-styled (no PresentationContext),
 * so it drops straight into the journey. Stacks full-width, scrolling with the page (2026-07 — was
 * a horizontal swipeable ribbon; switched to a vertical stack so each image previews larger, taking
 * better advantage of the journey card's own portrait-ish proportions instead of cramming landscape
 * images into a narrow horizontal strip).
 *
 * A PDF-backed item (isDrawing: true — the ribbon's PDF upload path in SketchManager.jsx) opens
 * ConceptDrawingModal in client mode instead of the plain lightbox, same as SketchWall.jsx's
 * openBoard() does for a presentation sketch board.
 */

import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'

export default function SketchRibbon({ sketches, projectId, token }) {
  const items = (sketches || []).filter(s => s && s.url)
  const [lb, setLb] = useState(null)   // lightbox index, or null

  const nav = useCallback((d) => {
    setLb(i => { if (i == null) return i; const n = i + d; return n >= 0 && n < items.length ? n : i })
  }, [items.length])

  useEffect(() => {
    if (lb == null) return
    // Capture phase + stopPropagation: JourneyShell has its own window-level (bubble-phase)
    // ArrowLeft/ArrowRight listener for moving between journey entries (laneNext/lanePrev) —
    // without both of these, that background nav fires on the same keypress as this lightbox's
    // own image nav. Same pattern as present/SketchWall.jsx's Lightbox.
    function onKey(e) {
      if (e.key === 'Escape') { e.stopPropagation(); setLb(null) }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); e.stopPropagation(); nav(-1) }
      else if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); nav(1) }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [lb, nav])

  if (!items.length) return null

  function openCard(s, i) {
    if (s.isDrawing) {
      window.dispatchEvent(new CustomEvent('open-concept-drawing-modal', {
        detail: { sketchId: s.id, projectId, title: s.title || 'Sketch', mode: 'client', journeyToken: token },
      }))
      return
    }
    setLb(i)
  }

  return (
    <>
      <div style={st.ribbon}>
        {items.map((s, i) => (
          <button key={s.id || i} onClick={() => openCard(s, i)} style={st.card} title={s.title || 'View'}>
            <div style={st.mat}>
              <img src={s.url} alt={s.title || ''} style={st.img} />
              {s.isDrawing && <span style={st.drawingBadge}>Drawing</span>}
            </div>
            {(s.title || s.type) && (
              <div style={st.cap}>
                {s.title && <div style={st.capTitle}>{s.title}</div>}
                {s.type && <div style={st.capType}>{s.type}</div>}
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Portaled to <body> — the journey entry card this ribbon lives inside uses
          backdrop-filter + a carousel transform, either of which creates a containing
          block that would trap this position:fixed overlay inside the card instead of
          covering the viewport. Same fix as present/Lightbox.jsx and PresentationLink.jsx. */}
      {lb != null && items[lb] && createPortal(
        <div style={st.overlay} onClick={() => setLb(null)}>
          <button style={{ ...st.arrow, left: 16, opacity: lb <= 0 ? 0.25 : 1 }} onClick={e => { e.stopPropagation(); nav(-1) }} disabled={lb <= 0}>‹</button>
          <div style={st.lbInner} onClick={e => e.stopPropagation()}>
            <img src={items[lb].url} alt={items[lb].title || ''} style={st.lbImg} />
            {(items[lb].title || items[lb].legend) && (
              <div style={st.lbCap}>
                {items[lb].title && <div style={st.lbTitle}>{items[lb].title}</div>}
                {items[lb].legend && <div style={st.lbLegend}>{items[lb].legend}</div>}
              </div>
            )}
          </div>
          <button style={{ ...st.arrow, right: 16, opacity: lb >= items.length - 1 ? 0.25 : 1 }} onClick={e => { e.stopPropagation(); nav(1) }} disabled={lb >= items.length - 1}>›</button>
          <button style={st.close} onClick={() => setLb(null)}>×</button>
        </div>,
        document.body
      )}
    </>
  )
}

const st = {
  ribbon: { display: 'flex', flexDirection: 'column', gap: 22, padding: '6px 2px 16px' },
  card: { width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' },
  mat: { position: 'relative', background: '#fff', border: '0.5px solid rgba(7,39,45,0.12)', borderRadius: 6, padding: 10, boxShadow: '0 6px 20px rgba(7,39,45,0.10)' },
  img: { width: '100%', aspectRatio: '3 / 2', objectFit: 'cover', borderRadius: 3, display: 'block', background: '#f4f7f8' },
  drawingBadge: { position: 'absolute', bottom: 18, right: 18, fontFamily: '"DM Mono",monospace', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#fff', background: 'rgba(7,39,45,0.82)', padding: '3px 7px', borderRadius: 3 },
  cap: { padding: '8px 2px 0' },
  capTitle: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: '#07272D', fontWeight: 500 },
  capType: { fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#7a9aaa', letterSpacing: '0.04em', textTransform: 'uppercase', marginTop: 2 },
  overlay: { position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(7,16,18,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4vh 6vw' },
  lbInner: { display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '100%', maxHeight: '100%' },
  lbImg: { maxWidth: '100%', maxHeight: '82vh', objectFit: 'contain', borderRadius: 4, boxShadow: '0 18px 60px rgba(0,0,0,0.5)' },
  lbCap: { marginTop: 14, textAlign: 'center', maxWidth: 720 },
  lbTitle: { fontFamily: '"DM Sans",sans-serif', fontSize: 15, color: '#fff', fontWeight: 500 },
  lbLegend: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'rgba(255,255,255,0.62)', marginTop: 4, lineHeight: 1.5 },
  arrow: { position: 'fixed', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#fff', fontSize: 44, cursor: 'pointer', lineHeight: 1, padding: '0 8px' },
  close: { position: 'fixed', top: 16, right: 20, background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 30, cursor: 'pointer', lineHeight: 1 },
}
